import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiArrowRight } from 'react-icons/fi';
import { useContent } from '../hooks/useContent';

const BusinessVertical = () => {
  const { vertical } = useParams();
  const { content: cms, loading } = useContent(`business_${vertical}`, null);

  const fallbackData = {
    sourcing: {
      title: 'Arvaron Sourcing', subtitle: 'Sourcing & Procurement',
      description: 'Strategic global sourcing solutions connecting markets, ensuring quality, and optimizing cost structures across industries.',
      image: 'https://images.pexels.com/photos/5860937/pexels-photo-5860937.jpeg',
      fullDescription: 'At Arvaron Sourcing, we connect businesses with quality suppliers across 15+ global markets. Our comprehensive sourcing solutions combine deep market intelligence with rigorous quality control, enabling our clients to access the best suppliers at optimal price points without compromising on quality standards.',
      keyServices: ['Global Supplier Network Management', 'Quality Assurance & Compliance', 'Cost Optimization Strategies', 'Supply Chain Risk Management', 'Market Intelligence & Analytics']
    },
    logistics: {
      title: 'Arvaron Logistics', subtitle: 'Logistics & Supply Chain',
      description: 'End-to-end global freight, warehousing, and distribution networks engineered for resilience and efficiency.',
      image: 'https://images.unsplash.com/photo-1653794280510-6d0c380449a7',
      fullDescription: 'Arvaron Logistics provides comprehensive supply chain solutions that combine advanced technology with operational excellence. Our global network spans 15+ countries, offering seamless freight forwarding, warehousing, and distribution services.',
      keyServices: ['Global Freight Forwarding', 'Warehousing & Distribution', 'Real-time Tracking & Analytics', 'Last-Mile Delivery Solutions', 'Supply Chain Optimization']
    },
    exports: {
      title: 'Arvaron Exports', subtitle: 'Imports & Exports',
      description: 'Facilitating seamless international trade with deep EXIM expertise, trade finance, and cross-border commerce solutions.',
      image: 'https://images.pexels.com/photos/9806482/pexels-photo-9806482.jpeg',
      fullDescription: 'Arvaron Exports specializes in facilitating international trade operations with comprehensive EXIM services. Our team provides end-to-end support for businesses looking to expand their global footprint.',
      keyServices: ['EXIM Documentation & Compliance', 'Trade Finance Solutions', 'Customs Clearance', 'Market Entry Strategy', 'International Trade Consulting']
    },
    technologies: {
      title: 'Arvaron Technologies', subtitle: 'IT Consulting & Software',
      description: 'Driving digital transformation through custom software development, IT consulting, and cloud innovation for enterprises.',
      image: 'https://images.unsplash.com/photo-1499951360447-b19be8fe80f5',
      fullDescription: 'Arvaron Technologies partners with enterprises to design and execute digital transformation strategies that drive measurable results. Our solutions combine cutting-edge technology with deep industry expertise.',
      keyServices: ['Custom Software Development', 'Cloud Infrastructure & Migration', 'Enterprise IT Consulting', 'System Integration', 'Digital Transformation Strategy']
    },
    digital: {
      title: 'Arvaron Digital', subtitle: 'Digital Marketing',
      description: 'Data-driven marketing technology, brand strategy, and performance campaigns for the modern enterprise.',
      image: 'https://images.unsplash.com/photo-1718220216044-006f43e3a9b1',
      fullDescription: 'Arvaron Digital delivers data-driven marketing solutions that combine strategic thinking with execution excellence. Our team helps businesses build brands, acquire customers, and drive measurable ROI.',
      keyServices: ['Performance Marketing', 'Brand Strategy & Development', 'Marketing Analytics', 'Content Marketing', 'Social Media Management']
    },
    infra: {
      title: 'Arvaron Infra', subtitle: 'Real Estate Development',
      description: 'Development and management of landmark commercial and residential properties across India\'s fastest-growing markets.',
      image: 'https://images.unsplash.com/photo-1527576539890-dfa815648363',
      fullDescription: 'Arvaron Infra develops premium commercial and residential properties that set new standards for quality and sustainability. Our projects are strategically located in high-growth markets across India.',
      keyServices: ['Commercial Real Estate Development', 'Residential Property Development', 'Property Management', 'Sustainable Building Practices', 'Real Estate Investment']
    },
    retail: {
      title: 'Arvaron Retail', subtitle: 'Retail Operations',
      description: 'Curated retail experiences and brand distribution connecting consumers with quality products across India and beyond.',
      image: 'https://images.pexels.com/photos/35782401/pexels-photo-35782401.jpeg',
      fullDescription: 'Arvaron Retail operates premium retail spaces and distribution networks that connect quality brands with discerning consumers. Our curated approach ensures exceptional shopping experiences.',
      keyServices: ['Retail Space Management', 'Brand Distribution', 'E-commerce Integration', 'Customer Experience Design', 'Retail Analytics']
    }
  };

  // CMS content maps: title→title, subtitle→subtitle, description→description, 
  // image→image, full_description→fullDescription, services→keyServices
  const data = cms ? {
    title: cms.title,
    subtitle: cms.subtitle,
    description: cms.description,
    image: cms.image,
    fullDescription: cms.full_description,
    keyServices: cms.services
  } : fallbackData[vertical];

  if (!data && !loading) {
    return (
      <div className="bg-black text-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-display text-3xl font-bold mb-4">Business Vertical Not Found</h1>
          <Link to="/businesses" className="text-[#17B9BE] hover:text-white transition-colors">Back to Businesses</Link>
        </div>
      </div>
    );
  }

  if (loading || !data) {
    return <div className="bg-black text-white min-h-screen flex items-center justify-center"><div className="text-white/30">Loading...</div></div>;
  }

  return (
    <div className="bg-black text-white" data-testid="business-vertical-page">
      <section className="relative h-[65vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={data.image} alt={data.title} className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 z-10 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <p className="text-xs uppercase tracking-[0.3em] text-[#17B9BE] mb-6" data-testid="vertical-subtitle">{data.subtitle}</p>
            <h1 className="font-display text-5xl md:text-7xl font-bold mb-6" data-testid="vertical-title">
              <span className="brand-gradient-text">{data.title}</span>
            </h1>
            <p className="text-lg text-white/50 max-w-3xl mx-auto leading-relaxed">
              {data.description}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-[#0a0a0a]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
            <p className="text-xs uppercase tracking-[0.3em] text-[#17B9BE] mb-4">Overview</p>
            <h2 className="font-display text-3xl font-bold mb-8">
              What We <span className="brand-gradient-text">Do</span>
            </h2>
            <p className="text-white/50 text-lg leading-relaxed">
              {data.fullDescription}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-black">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
            <p className="text-xs uppercase tracking-[0.3em] text-[#17B9BE] mb-4">Capabilities</p>
            <h2 className="font-display text-3xl font-bold mb-10">
              Key <span className="brand-gradient-text">Services</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {data.keyServices.map((service, index) => (
                <div
                  key={index}
                  className="bg-[#0a0a0a] p-6 rounded border border-white/5 hover:border-[#17B9BE]/20 transition-all duration-300"
                  data-testid={`service-${index}`}
                >
                  <div className="flex items-start">
                    <div className="w-8 h-8 rounded-sm brand-gradient-bg flex items-center justify-center text-black font-bold text-xs mr-4 flex-shrink-0">
                      {String(index + 1).padStart(2, '0')}
                    </div>
                    <h3 className="text-base font-semibold leading-relaxed pt-1">{service}</h3>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-32 bg-[#0a0a0a] relative overflow-hidden">
        <div className="absolute inset-0 hero-overlay" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
            <h2 className="font-display text-4xl font-bold mb-6">
              Partner with <span className="brand-gradient-text">{data.title}</span>
            </h2>
            <p className="text-white/40 text-lg mb-10 max-w-2xl mx-auto">
              Discover how we can help drive your business forward
            </p>
            <Link
              to="/contact"
              data-testid="vertical-cta-button"
              className="group px-10 py-4 brand-gradient-bg text-black font-semibold rounded-sm hover:opacity-90 transition-all inline-flex items-center justify-center text-sm uppercase tracking-wider"
            >
              Get in Touch
              <FiArrowRight className="ml-3 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BusinessVertical;
